import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';

import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { CatalogEntry, useCircuitProducts } from '../queries';
import { useAutocompleteFocus } from '../useAutocompleteFocus';
import { isCurrentSeason, productTitle, seasonOf, seriesOf, sortProducts } from '../circuits';

interface Props {
  label: string;
  value: CatalogEntry | null;
  onChange: (product: CatalogEntry | null) => void;
  /** Incluir temporadas pasadas (por defecto se ocultan: no tienen salidas). */
  includePastSeasons: boolean;
  width?: number | string;
}

/**
 * Circuito del catálogo del proveedor. La búsqueda es DIRIGIDA: sin producto
 * elegido no hay salidas que mostrar.
 *
 * El catálogo mezcla ~25k productos de temporadas viejas y vigentes, y solo las
 * vigentes tienen salidas — así que por defecto se ocultan las pasadas y se
 * ordena por temporada descendente. La temporada se muestra en cada opción
 * para que la elección sea informada.
 */
export function CircuitProductInput({
  label,
  value,
  onChange,
  includePastSeasons,
  width = 420,
}: Props) {
  const { t } = useTranslation();
  const [debouncedQuery, setQuery] = useDebouncedState('', 400);
  // `productTitle` es la MISMA etiqueta que usa `getOptionLabel` abajo: si
  // difirieran, MUI repondría el input contra la suya (#2226).
  const focus = useAutocompleteFocus({
    selectedLabel: value ? productTitle(value) : '',
    onQueryChange: setQuery,
  });
  const { data, isFetching } = useCircuitProducts(debouncedQuery);
  // Aislado (#2226): solo se enciende mientras el asesor tipea EN ESTE campo.
  const showLoading = focus.editing && isFetching && debouncedQuery.trim().length >= 3;

  const currentYear = new Date().getFullYear();

  const options = useMemo(() => {
    const all = data ?? [];
    const visible = includePastSeasons
      ? all
      : all.filter((entry) => isCurrentSeason(entry, currentYear));
    return sortProducts(visible).slice(0, 80);
  }, [data, includePastSeasons, currentYear]);

  /** Hubo resultados pero todos de temporadas pasadas: hay que decirlo. */
  const onlyPastSeasons =
    !includePastSeasons && (data?.length ?? 0) > 0 && options.length === 0;

  return (
    <Autocomplete
      size="small"
      sx={{ width }}
      options={options}
      loading={showLoading}
      value={value}
      onChange={(_event, product, reason) => {
        if (focus.commitValueChange(reason)) onChange(product);
      }}
      inputValue={focus.inputValue}
      onFocus={focus.onFocus}
      onBlur={focus.onBlur}
      onInputChange={focus.onInputChange}
      getOptionLabel={(option) => productTitle(option)}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      // El filtrado (texto en el hub, temporada acá) ya está hecho.
      filterOptions={(opts) => opts}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box display="flex" alignItems="center" gap={1} width="100%">
            <Typography variant="body2" flex={1}>
              {productTitle(option)}
            </Typography>
            {seriesOf(option) && (
              <Chip size="small" variant="outlined" label={seriesOf(option)} />
            )}
            <Chip
              size="small"
              variant="outlined"
              color={isCurrentSeason(option, currentYear) ? 'primary' : 'default'}
              label={seasonOf(option) || '—'}
            />
          </Box>
        </Box>
      )}
      noOptionsText={
        onlyPastSeasons
          ? t('travelServices.circuits.onlyPastSeasons')
          : debouncedQuery.trim().length < 3
            ? t('travelServices.circuits.typeToSearch')
            : t('travelServices.circuits.noProducts')
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={t('travelServices.circuits.productPlaceholder')}
          required
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {showLoading && <CircularProgress size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
